import { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Tooltip from '@mui/material/Tooltip'
import ChatWithSupportIcon from '@mui/icons-material/ChatBubbleOutline'
import SignOutIcon from '@mui/icons-material/Logout'
import ProfileAvatarIcon from '@mui/icons-material/Person'

import useAppStore from '$appStore'
import { useReactNavigate } from '$hooks'
import { logger, signout } from '$utils'

import FlyoutSurface from './FlyoutSurface'
import AppMenuHeader from './AppMenuHeader'
import VenueSection from './VenueSection'
import AccountSection from './AccountSection'
import HelpSection from './HelpSection'
import { showIntercom } from './intercom'
import { useIntercom } from './useIntercom'

/**
 * The app menu flyout is a menu to allow navigation to all the non-module areas
 * of Loaded. This is things like venue settings, a users profile, billing, etc.
 *
 * This top-level component wires the topbar triggers (the chat shortcut and the
 * profile avatar) to a responsive flyout surface, and assembles the flyout body
 * from its sections: who is signed in (`AppMenuHeader`), the current venue and
 * its actions (`VenueSection`), account actions (`AccountSection`), help links
 * (`HelpSection`), and sign out. It owns the open/close state and the close +
 * navigate / sign-out interactions the sections delegate to; each section owns
 * its own content.
 */
const AppMenuFlyout = () => {
  const userDetails = useAppStore((state) => state.userDetails)

  const reactNavigate = useReactNavigate()

  // Boots Intercom for the signed-in user and exposes the unread message count
  // so the chat launcher can show a badge.
  const { unreadCount } = useIntercom()

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null)

  const { firstName, lastName } = userDetails
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget)
  }

  // Cleanly close the flyout menu, used when navigating / acting on user
  // actions within the menu.
  const handleClose = () => {
    setAnchorElement(null)
  }

  // Wrapper to close the menu before navigating, avoiding it remaining open
  // when clicking an external link.
  const handleNavigate = (target: string) => {
    handleClose()
    reactNavigate(target)
  }

  const handleChatWithSupport = () => {
    handleClose()
    showIntercom()
  }

  // Cleanly sign a user out of the app
  const handleSignOut = () => {
    handleClose()
    logger.info('Signing out from the React topbar')
    signout()
  }

  return (
    <>
      <Tooltip title="Chat with support">
        <IconButton
          onClick={showIntercom}
          aria-label="Chat with support"
          sx={{
            // Hidden on mobile to reclaim the cramped right region - "Chat with
            // support" is still reachable from the flyout's Help section.
            display: { xs: 'none', smxl: 'inline-flex' },
            color: (theme) => theme.app.headerTextColor,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            marginLeft: 0.5,
            marginRight: 1,
          }}
        >
          <Badge
            variant="dot"
            invisible={unreadCount === 0}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                width: 12,
                height: 12,
                borderRadius: '50%',
              },
            }}
          >
            <ChatWithSupportIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title={fullName || 'Account'}>
        <IconButton
          onClick={handleOpen}
          aria-label="Open profile menu"
          sx={{
            padding: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: 'header.foreground',
              color: 'header.background',
            }}
          >
            <ProfileAvatarIcon sx={{ fontSize: 20 }} />
          </Avatar>
        </IconButton>
      </Tooltip>

      <FlyoutSurface anchorElement={anchorElement} onClose={handleClose}>
        <AppMenuHeader />

        <Divider />

        <VenueSection onNavigate={handleNavigate} />

        <AccountSection onNavigate={handleNavigate} />

        <Divider />

        <HelpSection
          onClose={handleClose}
          onChatWithSupport={handleChatWithSupport}
        />

        <Divider />

        <MenuList>
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <SignOutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </MenuList>
      </FlyoutSurface>
    </>
  )
}

export default AppMenuFlyout
